import DeviceDetector from 'device-detector-js'
import { ensureProperties } from '../utils/safe-stringify'
import { ClientJS } from 'clientjs'
import bigJson from 'big-json'

const TELEMETRY_SPLIT = 1

const parse = body => {
  try {
    return JSON.parse(body)
  } catch (e) {
    console.error('Failed to parse, trying to use bigJson')
    return bigJson.parse({ body })
  }
}
const stringify = body => {
  try {
    return JSON.stringify(body)
  } catch (e) {
    console.error('Failed to stringify, trying to use bigJson')
    return bigJson.stringify({ body })
  }
}

const wait = delay => new Promise(resolve => setTimeout(resolve, delay))

const raceTimeout = (action, options = {}) => {
  const { delay = 40000, catchError = true } = options

  const cancelSymbol = Symbol('cancelPromise')

  let $cancel
  let cancelled

  const cancel = new Promise(resolve => {
    $cancel = () => {
      cancelled = true
      resolve(cancelSymbol)
    }
  })

  const promise = Promise.race([
    cancel,
    typeof action === 'function' ? action() : action,
    wait(delay).then(() => ({ telemetryTimeout: delay })),
  ]).then(result => {
    if (result === cancelSymbol) throw new Error('Promise cancelled')
    return result
  })

  let result = catchError
    ? promise.catch(e => {
        if (cancelled) throw e
        setTimeout(() => window?.$nuxt?.$rollbar?.error(e), 100)
        console.error(e)
        return {
          telemetryError: true,
          telemetryErrorMessage: e.message ?? e ?? '?',
        }
      })
    : promise

  result.$cancel = $cancel

  return result
}

const allTimeout = (promises, options) =>
  Promise.all(promises.map(promise => raceTimeout(promise, options)))

const namedAllTimeout = async (indexedQueries, options) =>
  Object.fromEntries(
    await Promise.all(
      Object.entries(indexedQueries).map(async ([key, action]) => {
        const now = performance.now()
        const result = [key, await raceTimeout(action, options)]
        options?.progressReporter?.(`Collect named "${key}"`, { now })
        return result
      })
    )
  )

const hasFailed = obj => 'telemetryError' in obj || 'telemetryTimeout' in obj

const getValue = input =>
  raceTimeout(async () => {
    if (typeof input?.then === 'function') return await input
    switch (typeof input) {
      case 'function': {
        const result = input()
        return typeof result?.then === 'function' ? await result : result
      }
      default:
        return input
    }
  })

const getProperties = async (object, properties) =>
  !object
    ? null
    : Object.fromEntries(
        await Promise.all(
          properties.map(async key => [key, await getValue(object[key])])
        )
      )

const getEnv = () => {
  switch (window.location.host) {
    case 'coach.jbs.emiolo.com.br':
      return 'production'
    case 'coach-beta.jbs.emiolo.com.br':
      return 'beta'
    case 'localhost:3001':
      return 'development'
    default:
      return 'unknown'
  }
}

const getGeoLocationPosition = () =>
  new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(
      result => resolve(result),
      error => reject(error),
      {
        maximumAge: 60000,
        enableHighAccuracy: true,
        timeout: 10000,
      }
    )
  )

export const getGeoLocationError = code => {
  switch (code) {
    case -1:
      return 'GEOLOCATION_NOT_SUPPORTED'
    case 0:
      return 'SUCCESS'
    case 1:
      return 'PERMISSION_DENIED'
    case 2:
      return 'POSITION_UNAVAILABLE'
    case 3:
      return 'TIMEOUT'
    default:
      return '?'
  }
}

const getGeoLocation = async () => {
  if (!('geolocation' in navigator))
    return { position: null, status: getGeoLocationError(-1), statusCode: -1 }
  try {
    const position = await raceTimeout(getGeoLocationPosition, {
      catchError: false,
    })
    if ('telemetryTimeout' in position)
      return {
        position: null,
        status: '?',
        statusCode: '?',
        telemetryTimeout: position.telemetryTimeout,
      }

    return {
      position: {
        coords: await getProperties(position.coords, [
          'latitude',
          'longitude',
          'altitude',
          'accuracy',
          'altitudeAccuracy',
          'heading',
          'speed',
        ]),
        timestamp: new Date(position.timestamp).toISOString(),
      },
      status: 'SUCCESS',
      statusCode: 0,
    }
  } catch (e) {
    return {
      position: null,
      status: getGeoLocationError(e.code),
      statusCode: e.code ?? '?',
      error: e.message ?? e,
    }
  }
}

const getDevice = userAgent => {
  try {
    const deviceDetector = new DeviceDetector()
    return deviceDetector.parse(userAgent)
  } catch (e) {
    return null
  }
}

const getServiceWorker = async () => {
  const empty = {
    ready: null,
    registrations: [],
    compatible: false,
  }
  if (!navigator.serviceWorker) return empty
  const registrations = await raceTimeout(
    navigator.serviceWorker.getRegistrations(),
    5000
  )
  if (!registrations.length) return empty
  return {
    // Must wrap with ensureProperties: ServiceWorkerRegistration is a live browser object
    // that cannot be cloned into IndexedDB (causes DataCloneError).
    ready: ensureProperties(await raceTimeout(navigator.serviceWorker?.ready, 30000)),
    registrations: ensureProperties(registrations),
    compatible: true,
  }
}

export default (ctx, inject) => {
  let runningPromise = null
  let syncPromise = null
  let running = false
  let firstExecution = true

  const syncEntries = async () => {
    return Promise.resolve()
    /*
    await ctx.store.restored?.catch(e => console.error(e))
    if (!window.navigator.onLine) return
    const { store } = ctx
    if (syncPromise) return
    syncPromise = new Promise((resolve, reject) => {
      const worker = new Worker('./telemetry-worker', { type: 'module' })
      let trips = 0
      let trip = 0
      const runTrip = async () => {
        trips = Math.ceil(
          store.state.telemetry.entries.length / TELEMETRY_SPLIT
        )
        if (trips <= 0) {
          syncPromise = null
          resolve(true)
          console.info('Telemetry - sync - all clear')
          return
        }
        console.info(`Telemetry - sync trip ${trip} - start (${trips} to go)`)
        const entries = store.getters['telemetry/top'](TELEMETRY_SPLIT)
        worker.postMessage({ entries: JSON.parse(JSON.stringify(entries)) })
      }
      worker.onmessage = event => {
        if (event.data === false) {
          console.info(`Telemetry - sync trip ${trip} - failed`)
          pushEvent({
            category: 'telemetry',
            name: 'sync',
            action: 'push',
            trip,
          })
          syncPromise = null
          reject('failed')
          return
        }
        console.info(`Telemetry - sync trip ${trip} - success`)
        store.commit('telemetry/popEntries', TELEMETRY_SPLIT)
        trip += 1
        runTrip()
      }
      runTrip()
    })
    return syncPromise
    */
  }

  // syncEntries()

  const { userAgent } = window.navigator
  setInterval(() => {
    firstExecution = true
  }, 600_000)

  const pushEvent = (_name, _payload = {}) => null
  /* {
    const $nuxt = window.$nuxt
    try {
      const payload = typeof _name === 'object' ? _name : _payload
      const name = typeof _name === 'object' ? payload.name : _name
      const {
        category = null,
        error = null,
        result = null,
        action = null,
        name: $name,
        ...metadata
      } = payload
      const isError = error instanceof Error || error instanceof ErrorEvent
      const event = ensureProperties({
        name,
        category,
        action,
        result: result ?? (error ? 'error' : action ? 'success' : null),
        metadata,
        isError,
        context: {
          href: window.location.href,
          route: ctx.route.name,
          hidden: document.hidden,
          offline: !navigator.onLine,
        },
        error: isError
          ? {
              message: error.message ?? error.msg ?? error.toString?.() ?? null,
              status: error.status ?? null,
              stack: error.stack ?? error.error?.stack ?? null,
              name: error.name ?? null,
              filename: error.filename ?? null,
              colno: error.colno ?? null,
              lineno: error.lineno ?? null,
            }
          : error,
        timestamp: new Date().toISOString(),
      })
      ctx.store.commit('telemetry/pushEvent', event)
      if (getEnv() === 'development') {
        const logTag = [
          'Telemetry log',
          category ? ` (${category})` : '',
          ': ',
          name,
          action ? ` - ${action}` : '',
        ].join('')
        const logMethod = isError ? 'error' : 'log'
        console[logMethod](logTag, event, error)
        return
      }
      if (isError) $nuxt?.$rollbar?.error?.(error)
    } catch (error) {
      console.error('Failed pushing telemetry event')
      $nuxt?.$rollbar?.error?.(error)
    }
  } */

  let clientJS
  let fingerprint

  try {
    clientJS = new ClientJS()
  } catch (error) {
    pushEvent({
      category: 'telemetry',
      name: 'ClientJS',
      action: 'setup',
      error,
    })
  }

  const getFingerprint = () => {
    if (!clientJS) return null
    if (fingerprint) return fingerprint
    fingerprint = {}
    const collectables = [
      'getFingerprint',
      'getUserAgent',
      'getBrowser',
      'getBrowserVersion',
      'getBrowserMajorVersion',
      'isIE',
      'isChrome',
      'isFirefox',
      'isSafari',
      'isOpera',
      'getEngine',
      'getEngineVersion',
      'getOS',
      'getOSVersion',
      'isWindows',
      'isMac',
      'isLinux',
      'isUbuntu',
      'isSolaris',
      'getDevice',
      'getDeviceType',
      'getDeviceVendor',
      'getCPU',
      'isMobile',
      'isMobileMajor',
      'isMobileAndroid',
      'isMobileOpera',
      'isMobileWindows',
      'isMobileBlackBerry',
      'isMobileIOS',
      'isIphone',
      'isIpad',
      'isIpod',
      'getColorDepth',
      'getCurrentResolution',
      'getAvailableResolution',
      'getDeviceXDPI',
      'getDeviceYDPI',
      'getPlugins',
      'getMimeTypes',
      'isMimeTypes',
      'getFonts',
      'isLocalStorage',
      'isSessionStorage',
      'isCookie',
      'getTimeZone',
      'getLanguage',
      'getSystemLanguage',
      'isCanvas',
    ]
    collectables.forEach(key => {
      try {
        fingerprint[key] = clientJS[key]() ?? null
      } catch (error) {
        pushEvent({
          category: 'telemetry',
          name: 'ClientJS',
          action: `load:${key}`,
          error,
        })
      }
    })
    const collected = Object.keys(fingerprint).length
    pushEvent({
      category: 'telemetry',
      name: 'ClientJS',
      action: 'collectFingerprints',
      result: collected ? 'success' : 'error',
      collected,
    })
    return fingerprint
  }

  const collectTelemetry = async (telemetryId, progressReporter) => {
    progressReporter('collectStart')
    const collectStart = Date.now()
    const { store, app } = ctx
    const { user, tokenMetadata } = store.state.auth
    progressReporter('getBasicVariables')
    await raceTimeout(() => store.dispatch('version/fetch'), { delay: 2000 })
    progressReporter('fetchVersion')
    if (firstExecution && user) {
      try {
        const response = await app.$axios.post(
          `/v3/telemetry/sync/${user._id}/should-full-sync`
        )
        store.commit('telemetry/setFullSync', response.data)
      } catch (e) {
        console.error('Error fetching full-sync', e)
        ctx.app.$rollbar?.error(e)
      }
      firstExecution = false
      progressReporter('checkFullsync')
    }

    let device
    try {
      device = getDevice(userAgent)
    } catch (e) {
      device = null
    }
    progressReporter('collectDevicedData')

    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection
    const versionTag = window.$versionTag

    const telemetry = await namedAllTimeout(
      {
        serviceWorker: getServiceWorker(),
        geolocation: getGeoLocation(),
        offlineSync: store.state.offline,
        location: getProperties(window.location, [
          'href',
          'protocol',
          'host',
          'hostname',
          'origin',
        ]),
        battery: navigator
          .getBattery?.()
          .then(manager =>
            getProperties(manager, [
              'charging',
              'chargingTime',
              'dischargingTime',
              'level',
            ])
          ),
        connection: getProperties(connection, [
          'downlink',
          'downlinkMax',
          'effectiveType',
          'rtt',
          'saveData',
          'type',
        ]),
        navigator: getProperties(window.navigator, [
          'buildID',
          'deviceMemory',
          'language',
          'maxTouchPoints',
          'onLine',
          'hardwareConcurrency',
          'userAgent',
        ]),
        window: getProperties(window, ['closed', 'devicePixelRatio']),
      },
      { progressReporter }
    )

    progressReporter('collectNamedPromises')

    const collectEnd = Date.now()
    const collectDuration = collectEnd - collectStart
    const result = {
      ...telemetry,
      user,
      device,
      fingerprint: getFingerprint(),
      versionTag,
      token: app.$api.services.server.token,
      tokenMetadata,
      tokenMetadataHuman: {
        iat: tokenMetadata?.iat
          ? new Date(tokenMetadata.iat * 1000).toISOString()
          : null,
        exp: tokenMetadata?.exp
          ? new Date(tokenMetadata.exp * 1000).toISOString()
          : null,
        expired: tokenMetadata?.exp
          ? new Date().getTime() > tokenMetadata.exp * 1000
          : null,
      },
      env: getEnv(),
      timestamp: new Date().toISOString(),
      telemetry: {
        lastClear: store.state.telemetry.lastClear,
        shouldFullSync: store.state.telemetry.shouldFullSync,
        entries: store.state.telemetry.entries?.length ?? 0,
      },
      coaching: {
        activeCoaching: !!store.state.coaching.current,
        lastCoaching: store.state.coaching.lastCoaching,
        creating: !!store.state.coaching.creating,
        offlineSyncCount: Object.values(store.state.coaching.offlineSync || {})
          .length,
      },
      versionControl: {
        ...store.state.version,
        updated: store.getters['version/updated'],
      },
      sync: store.state.offline,
      lockedAuth: store.state.auth.locked,
      lockedAuthReasons: store.state.auth.lockReasons,
      timers: {
        collectStart,
        collectEnd,
        collectDuration,
      },
      rollbar: !window?.$nuxt?.$rollbar
        ? null
        : getProperties(window?.$nuxt?.$rollbar?.options, [
            'version',
            'environment',
            'endpoint',
            'captureIp',
            'captureUncaught',
            'captureUnhandledRejections',
            'enabled',
            'logLevel',
            'reportLevel',
            'transmit',
            'uncaughtErrorLevel',
            'accessToken',
          ]),
    }

    progressReporter('joinData')
    if (store.state.telemetry.shouldFullSync) {
      const { telemetry, ...state } = store.state
      Object.assign(result, {
        fullSync: {
          storeState: await parse(await stringify(state)),
        },
      })
      progressReporter('collectFullSync')
    }

    result.telemetryStages = progressReporter.stages
    progressReporter('end')
    return result
  }

  const handler = async force => {
    await ctx.store.restored?.catch(e => console.error(e))
    // syncEntries()

    const runId = Math.trunc(Math.random() * 1000000)
    let start = performance.now()
    const stages = []
    const progressReporter = (stage, reference) => {
      const now = performance.now()
      const elapsed = now - start
      const last = reference ?? [...stages].pop()
      const duration = last ? now - last.now : elapsed
      const newStage = { stage, now, elapsed, duration }
      stages.push(newStage)
    }
    Object.defineProperty(progressReporter, 'stages', { get: () => stages })
    progressReporter('setupReporter')

    if (runningPromise && force) {
      runningPromise.$cancel()
      runningPromise = null
      progressReporter('cancelRunningTelemetry')
    }
    if (runningPromise) return runningPromise
    const { store } = ctx
    let telemetryPromise
    telemetryPromise = raceTimeout(collectTelemetry(runId, progressReporter), {
      delay: 50000,
    })
    runningPromise = telemetryPromise
    progressReporter('setupPromise')
    try {
      progressReporter('setupSync')
      store.commit('telemetry/running', true)
      progressReporter('setupRunner')
      const telemetry = await runningPromise
      store.commit('telemetry/setDevice', telemetry.device)
      store.commit('telemetry/push', telemetry)
    } catch (error) {
      console.error(error)
      pushEvent({
        category: 'telemetry',
        name: 'handler',
        action: 'run',
        error,
      })
      store.commit('telemetry/failed', true)
    }
    if (runningPromise !== telemetryPromise) return
    // syncEntries()
    store.commit('telemetry/running', false)
    runningPromise = null
  }

  Object.defineProperty(handler, 'pushEvent', {
    get: () => pushEvent,
  })
  Object.defineProperty(handler, 'running', {
    get: () => runningPromise,
  })
  Object.defineProperty(handler, 'getDevice', {
    get: () => () => getDevice(userAgent),
  })
  Object.defineProperty(handler, 'cancel', {
    get: () => () => {
      if (runningPromise) {
        runningPromise.$cancel()
        return true
      }
      return false
    },
  })
  /*
  window.addEventListener('error', error =>
    pushEvent({
      category: 'window',
      name: 'event',
      action: 'error',
      error,
    })
  )
  const windowEvents = ['close', 'blur', 'focus', 'load', 'unload']
  windowEvents.forEach(action =>
    window.addEventListener(action, () =>
      pushEvent({
        category: 'window',
        name: 'event',
        action,
      })
    )
  )
  */
  inject('telemetry', handler)
  ctx.$telemetry = handler
  ctx.app.$telemetry = handler
  // setInterval(handler, 60000)
  ctx.store.commit('telemetry/running', false)
  if (
    ctx.store.state.telemetry.events.length > 1 ||
    ctx.store.state.telemetry.entries.length
  )
    ctx.store.commit('telemetry/reset')
  // handler()
  window.addEventListener('online', handler)
  window.addEventListener('offline', handler)
}
